import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { searchUserProfiles } from './userProfileService';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export interface DirectoryPerson {
  email: string;
  name: string | null;
  title?: string | null;
  photoUrl?: string | null;
}

export interface DirectoryResult {
  people: DirectoryPerson[];
  /** False when the Google directory could not be queried — results came from the fallback. */
  fromDirectory: boolean;
}

/**
 * Look up FINN people for the interviewer picker.
 *
 * Primary source is the Google Workspace directory, so EVERY colleague is
 * findable. When that is unavailable (missing scope, API error) it falls back to
 * the Firestore `users` collection, which only holds people who have signed into
 * Spaces — narrower, but far better than an empty picker. The caller is told
 * which source answered so the UI can say so.
 */
export async function searchPeople(query: string, limit = 8): Promise<DirectoryResult> {
  const q = query.trim();
  if (q.length >= 2) {
    try {
      const res = await authenticatedFetch(
        `${PROXY_URL}/api/directory/people?q=${encodeURIComponent(q)}`
      );
      if (res.ok) {
        const data = (await res.json()) as { people: DirectoryPerson[]; available: boolean };
        if (data.available && data.people.length > 0) {
          return { people: data.people.slice(0, limit), fromDirectory: true };
        }
        // `available` with no hits is a real "no such person" — don't mask it by
        // silently showing a different, narrower list.
        if (data.available) return { people: [], fromDirectory: true };
      }
    } catch {
      // Fall through to the local list.
    }
  }

  const profiles = await searchUserProfiles(q, limit);
  return {
    people: profiles.map((p) => ({
      email: p.email,
      name: p.displayName || null,
      photoUrl: p.photoURL,
    })),
    fromDirectory: false,
  };
}
